import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import '../Customers/Customers.css';

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({
    name: '',
    username: '',
    email: '',
    password: '',
    role_id: '',
  });
  const [createUserError, setCreateUserError] = useState('');
  const [createUserLoading, setCreateUserLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersResponse, rolesResponse] = await Promise.all([
        adminAPI.getUsers(),
        adminAPI.getRoles(),
      ]);
      setUsers(usersResponse.users);
      setRoles(rolesResponse.roles);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load users');
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRoleId) => {
    try {
      await adminAPI.updateUserRole(userId, newRoleId);
      await loadData();
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update user role');
      console.error('Error updating role:', err);
    }
  };

  const handleStatusToggle = async (userId, currentStatus) => {
    try {
      await adminAPI.updateUserStatus(userId, !currentStatus);
      await loadData();
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update user status');
      console.error('Error updating status:', err);
    }
  };

  const handleViewDetails = async (userId) => {
    try {
      const response = await adminAPI.getUser(userId);
      setSelectedUser(response.user);
      setShowUserDetails(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load user details');
      console.error('Error loading user details:', err);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreateUserError('');
    setCreateUserLoading(true);

    try {
      await adminAPI.createUser(createUserForm);
      setShowCreateUser(false);
      setCreateUserForm({
        name: '',
        username: '',
        email: '',
        password: '',
        role_id: '',
      });
      await loadData();
      setError('');
    } catch (err) {
      const errors = err.response?.data?.errors;
      if (errors) {
        const firstError = Object.values(errors)[0];
        setCreateUserError(Array.isArray(firstError) ? firstError[0] : firstError);
      } else {
        setCreateUserError(err.response?.data?.message || 'Failed to create user');
      }
      console.error('Error creating user:', err);
    } finally {
      setCreateUserLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-main-wrapper">
          <main className="dashboard-content">
            <div className="customer-list-container">
              <div className="loading">Loading users...</div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-main-wrapper">
        <main className="dashboard-content">
          <div className="customer-list-container">
            <div className="customer-list-header">
              <h1>User Management</h1>
              <button
                className="btn btn-primary"
                onClick={() => setShowCreateUser(true)}
              >
                + Create User
              </button>
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <div className="customer-table-container">
              <table className="customer-table">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Devices</th>
                    <th>Last Device</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="no-data">
                        No users found
                      </td>
                    </tr>
                  ) : (
                    users.map((user) => (
                      <tr key={user.id}>
                        <td style={{ fontWeight: 600, color: '#2196F3' }}>{user.username}</td>
                        <td>{user.name}</td>
                        <td>{user.email}</td>
                        <td>
                          <select
                            value={user.role.id}
                            onChange={(e) => handleRoleChange(user.id, parseInt(e.target.value))}
                            style={{
                              padding: '6px 10px',
                              border: '1px solid #ced4da',
                              borderRadius: '4px',
                              fontSize: '14px',
                              cursor: 'pointer'
                            }}
                          >
                            {roles.map((role) => (
                              <option key={role.id} value={role.id}>
                                {role.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <button
                            onClick={() => handleStatusToggle(user.id, user.is_active)}
                            className={`badge ${user.is_active ? 'badge-success' : 'badge-secondary'}`}
                            style={{ cursor: 'pointer', border: 'none' }}
                          >
                            {user.is_active ? 'Active' : 'Inactive'}
                          </button>
                        </td>
                        <td style={{ textAlign: 'center', fontWeight: 600 }}>{user.devices_count}</td>
                        <td>
                          {user.most_recent_device ? (
                            <div style={{ fontSize: '13px' }}>
                              <div style={{ fontWeight: 500 }}>{user.most_recent_device.device_name}</div>
                              <div style={{ color: '#6c757d', fontSize: '12px' }}>
                                {formatDate(user.most_recent_device.last_used_at)}
                              </div>
                            </div>
                          ) : (
                            <span style={{ color: '#999', fontStyle: 'italic' }}>No devices</span>
                          )}
                        </td>
                        <td className="actions-cell">
                          <button
                            onClick={() => handleViewDetails(user.id)}
                            className="btn btn-sm btn-info"
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>

      {/* User Details Modal */}
      {showUserDetails && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowUserDetails(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>User Details: {selectedUser.username}</h2>
              <button
                className="modal-close"
                onClick={() => setShowUserDetails(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <h3>Account Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Name</label>
                    <div className="detail-value">{selectedUser.name}</div>
                  </div>
                  <div className="detail-item">
                    <label>Username</label>
                    <div className="detail-value">{selectedUser.username}</div>
                  </div>
                  <div className="detail-item">
                    <label>Email</label>
                    <div className="detail-value">{selectedUser.email}</div>
                  </div>
                  <div className="detail-item">
                    <label>Role</label>
                    <div className="detail-value">{selectedUser.role.name}</div>
                  </div>
                  <div className="detail-item">
                    <label>Status</label>
                    <div className="detail-value">
                      <span className={`badge ${selectedUser.is_active ? 'badge-success' : 'badge-secondary'}`}>
                        {selectedUser.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                  <div className="detail-item">
                    <label>Created</label>
                    <div className="detail-value">{formatDate(selectedUser.created_at)}</div>
                  </div>
                  <div className="detail-item">
                    <label>Email Verified</label>
                    <div className="detail-value">{formatDate(selectedUser.email_verified_at)}</div>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3>Enrolled Devices ({selectedUser.devices.length})</h3>
                {selectedUser.devices.length === 0 ? (
                  <div className="no-data" style={{ padding: '20px' }}>No devices enrolled</div>
                ) : (
                  <div className="devices-grid">
                    {selectedUser.devices.map((device, index) => (
                      <div key={device.id} className="device-card">
                        <div className="device-card-header">
                          <span style={{ fontWeight: 600 }}>Device #{index + 1}</span>
                          <span className="badge badge-secondary">{device.device_type || 'Unknown'}</span>
                        </div>
                        <div className="device-card-body">
                          <div className="detail-grid">
                            <div className="detail-item">
                              <label>Name</label>
                              <div className="detail-value">{device.device_name}</div>
                            </div>
                            <div className="detail-item">
                              <label>Browser</label>
                              <div className="detail-value">{device.browser_name || 'Unknown'}</div>
                            </div>
                            <div className="detail-item">
                              <label>Platform</label>
                              <div className="detail-value">{device.platform_name || 'Unknown'}</div>
                            </div>
                            <div className="detail-item">
                              <label>Last IP</label>
                              <div className="detail-value">{device.last_ip}</div>
                            </div>
                            <div className="detail-item">
                              <label>Last Used</label>
                              <div className="detail-value">{formatDate(device.last_used_at)}</div>
                            </div>
                            <div className="detail-item">
                              <label>Verified</label>
                              <div className="detail-value">{formatDate(device.verified_at)}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowUserDetails(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateUser && (
        <div className="modal-overlay" onClick={() => setShowCreateUser(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New User</h2>
              <button
                className="modal-close"
                onClick={() => setShowCreateUser(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleCreateUser}>
                <div className="form-group">
                  <label htmlFor="create-name">Name</label>
                  <input
                    type="text"
                    id="create-name"
                    value={createUserForm.name}
                    onChange={(e) => setCreateUserForm({ ...createUserForm, name: e.target.value })}
                    required
                    disabled={createUserLoading}
                    autoComplete="name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="create-username">Username</label>
                  <input
                    type="text"
                    id="create-username"
                    value={createUserForm.username}
                    onChange={(e) => setCreateUserForm({ ...createUserForm, username: e.target.value })}
                    required
                    disabled={createUserLoading}
                    autoComplete="username"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="create-email">Email</label>
                  <input
                    type="email"
                    id="create-email"
                    value={createUserForm.email}
                    onChange={(e) => setCreateUserForm({ ...createUserForm, email: e.target.value })}
                    required
                    disabled={createUserLoading}
                    autoComplete="email"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="create-password">Password</label>
                  <input
                    type="password"
                    id="create-password"
                    value={createUserForm.password}
                    onChange={(e) => setCreateUserForm({ ...createUserForm, password: e.target.value })}
                    required
                    disabled={createUserLoading}
                    autoComplete="new-password"
                    minLength="8"
                  />
                  <small className="form-hint">Minimum 8 characters</small>
                </div>

                <div className="form-group">
                  <label htmlFor="create-role">Role</label>
                  <select
                    id="create-role"
                    value={createUserForm.role_id}
                    onChange={(e) => setCreateUserForm({ ...createUserForm, role_id: e.target.value })}
                    required
                    disabled={createUserLoading}
                  >
                    <option value="">Select a role</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </div>

                {createUserError && (
                  <div className="error-message">
                    {createUserError}
                  </div>
                )}

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowCreateUser(false)}
                    disabled={createUserLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={createUserLoading}
                  >
                    {createUserLoading ? 'Creating...' : 'Create User'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;
